import { useTranslation } from 'react-i18next';

import {
  NOT_ASSESSED_REASONS,
  type NotAssessedReason,
} from '../framework';

type Props = {
  readonly value: NotAssessedReason | null;
  readonly onChange: (reason: NotAssessedReason) => void;
  readonly ariaLabel: string;
  readonly disabled?: boolean;
};

/**
 * Secondary picker shown when the user selects 'not-assessed' on a subdomain.
 *
 * Captures the practical-meaning reason: not applicable to this whānau,
 * the whānau declined to discuss this subdomain, or the kaimahi has not yet
 * had the opportunity to observe. Per ADR-005 §2.6.
 *
 * Styles share the StatePicker.css module since the reason picker is
 * conceptually a sub-control of the state picker.
 */
export function NotAssessedReasonPicker({
  value,
  onChange,
  ariaLabel,
  disabled = false,
}: Props) {
  const { t } = useTranslation();

  // Translate kebab-case reason ID to camelCase i18n key.
  const i18nKey = (reason: NotAssessedReason): string => {
    switch (reason) {
      case 'not-applicable':
        return 'notApplicable';
      case 'declined':
        return 'declined';
      case 'not-yet-assessed':
        return 'notYetAssessed';
    }
  };

  return (
    <div role="group" aria-label={ariaLabel} className="reason-picker">
      <span className="reason-picker__label">{t('preview.reasonLabel')}</span>
      {NOT_ASSESSED_REASONS.map((reason) => {
        const isSelected = value === reason;
        return (
          <button
            key={reason}
            type="button"
            aria-pressed={isSelected}
            disabled={disabled}
            className="reason-picker__option"
            onClick={() => onChange(reason)}
          >
            {t(`framework.notAssessedReason.${i18nKey(reason)}`)}
          </button>
        );
      })}
    </div>
  );
}

export default NotAssessedReasonPicker;
